import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  MapPin,
  Building,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { Department, Landmark, Route, RouteSegment } from "../types";

interface POI {
  id: number;
  poiName: string;
  points: string;
  longLatPoints: number[][];
}

interface Navigation3DPanelProps {
  selectedDestination?: Department | Landmark | null;
  onBack: () => void;
  onStartingPointChange: (poi: POI) => void;
  selectedStartingPoint?: POI | null;
  kioskData?: any;
  route?: Route | null;
  departments: Department[];
  landmarks: Landmark[];
  onSelectDepartment: (dept: Department) => void;
  onSelectLandmark: (landmark: Landmark) => void;
}

const Navigation3DPanel: React.FC<Navigation3DPanelProps> = ({
  selectedDestination,
  onBack,
  onStartingPointChange,
  selectedStartingPoint,
  kioskData,
  route,
  departments,
  landmarks,
  onSelectDepartment,
  onSelectLandmark,
}) => {
  const [pois, setPois] = useState<POI[]>([]);
  const [destinationSearchQuery, setDestinationSearchQuery] = useState("");
  const [showDestinationDropdown, setShowDestinationDropdown] = useState(false);
  const [landmarksCollapsed, setLandmarksCollapsed] = useState(true);
  const [departmentsCollapsed, setDepartmentsCollapsed] = useState(true);

  // Load POI data for coordinate matching
  useEffect(() => {
    fetch("/pois-data.json")
      .then((res) => res.json())
      .then((data) => {
        if (data.data) {
          // Filter out RETAIL POIs as requested
          const filteredPois = data.data.filter(
            (poi: POI) => !poi.poiName.toLowerCase().includes("retail"),
          );
          setPois(filteredPois);
          console.log(
            `✅ Loaded ${filteredPois.length} POIs for coordinate matching`,
          );
        }
      })
      .catch((err) => console.error("Failed to load POIs:", err));
  }, []);

  // Auto-populate destination field when selectedDestination changes
  useEffect(() => {
    if (selectedDestination && !destinationSearchQuery) {
      setDestinationSearchQuery(selectedDestination.name);
    }
  }, [selectedDestination, destinationSearchQuery]);

  // Check if destination coordinates match POI coordinates
  const getCoordinateStatus = (destination: Department | Landmark) => {
    const matchingPOI = pois.find(
      (poi) =>
        poi.poiName.toLowerCase().includes(destination.name.toLowerCase()) ||
        destination.name.toLowerCase().includes(poi.poiName.toLowerCase()),
    );

    if (matchingPOI) {
      return {
        status: "accurate",
        message: "✅ Using accurate POI coordinates",
        poi: matchingPOI,
      };
    } else {
      return {
        status: "legacy",
        message: "⚠️ Using legacy database coordinates",
        poi: null,
      };
    }
  };

  return (
    <div className="w-full md:w-96 h-full flex flex-col bg-white border-r border-gray-200">
      {/* Navigation Steps Section - Only show when route exists */}
      {route &&
        route.segments &&
        route.segments.length > 0 &&
        selectedDestination && (
          <div className="border-b border-gray-200 bg-white">
            {/* Destination Header */}
            <div className="p-4 bg-gray-50 border-b border-gray-200">
              <div className="flex items-start gap-3">
                {/* Icon Column with Dotted Line */}
                <div className="flex flex-col items-center pt-3">
                  {/* Empty Circle for Origin */}
                  <div className="w-4 h-4 rounded-full border-2 border-gray-500 bg-white"></div>
                  {/* Dotted Line */}
                  <div className="w-0.5 h-8 border-l-2 border-dotted border-gray-400 my-1"></div>
                  {/* Red Location Pin for Destination */}
                  <MapPin size={20} className="text-red-500" />
                </div>

                {/* Input Fields Column */}
                <div className="flex-1 space-y-2">
                  {/* Starting Point - Auto-populated from SAB ATM - Gate 1 (read-only) */}
                  <input
                    type="text"
                    value={(() => {
                      const startingPOI = pois.find(
                        (poi) => poi.poiName === "SAB ATM - Gate 1",
                      );
                      return startingPOI
                        ? startingPOI.poiName
                        : kioskData?.name || "Your Location";
                    })()}
                    readOnly
                    className="w-full bg-gray-100 border border-gray-300 rounded-lg px-3 py-2 text-sm cursor-not-allowed"
                    title="Starting point is set to SAB ATM - Gate 1"
                  />

                  {/* Destination Field with Search */}
                  <div className="relative">
                    <input
                      type="text"
                      value={destinationSearchQuery}
                      onChange={(e) => {
                        setDestinationSearchQuery(e.target.value);
                        setShowDestinationDropdown(true);
                      }}
                      onFocus={() => setShowDestinationDropdown(true)}
                      placeholder="Search destination..."
                      className="w-full bg-white border border-gray-300 rounded-lg px-3 py-2 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />

                    {/* Dropdown for search results */}
                    {showDestinationDropdown && destinationSearchQuery && (
                      <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-y-auto z-50">
                        {/* Landmarks */}
                        {landmarks
                          .filter((l) =>
                            l.name
                              .toLowerCase()
                              .includes(destinationSearchQuery.toLowerCase()),
                          )
                          .map((landmark) => (
                            <button
                              key={landmark.id}
                              onClick={() => {
                                onSelectLandmark(landmark);
                                setDestinationSearchQuery(landmark.name);
                                setShowDestinationDropdown(false);
                              }}
                              className="w-full text-left px-3 py-2 hover:bg-blue-50 transition-colors border-b border-gray-100"
                            >
                              <p className="text-sm font-semibold text-gray-900">
                                {landmark.name}
                              </p>
                              <p className="text-xs text-gray-500">Landmark</p>
                            </button>
                          ))}

                        {/* Departments */}
                        {departments
                          .filter((d) =>
                            d.name
                              .toLowerCase()
                              .includes(destinationSearchQuery.toLowerCase()),
                          )
                          .map((dept) => (
                            <button
                              key={dept.id}
                              onClick={() => {
                                onSelectDepartment(dept);
                                setDestinationSearchQuery(dept.name);
                                setShowDestinationDropdown(false);
                              }}
                              className="w-full text-left px-3 py-2 hover:bg-blue-50 transition-colors border-b border-gray-100"
                            >
                              <p className="text-sm font-semibold text-gray-900">
                                {dept.name}
                              </p>
                              <p className="text-xs text-gray-500">
                                Department
                              </p>
                            </button>
                          ))}

                        {/* No results */}
                        {landmarks.filter((l) =>
                          l.name
                            .toLowerCase()
                            .includes(destinationSearchQuery.toLowerCase()),
                        ).length === 0 &&
                          departments.filter((d) =>
                            d.name
                              .toLowerCase()
                              .includes(destinationSearchQuery.toLowerCase()),
                          ).length === 0 && (
                            <div className="px-3 py-4 text-center text-sm text-gray-500">
                              No destinations found
                            </div>
                          )}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Coordinate Status Indicator */}
              {selectedDestination && (
                <div className="mt-2 px-3">
                  <div className="text-xs text-gray-500">
                    {getCoordinateStatus(selectedDestination).message}
                  </div>
                </div>
              )}
            </div>

            {/* Route Steps */}
            <div className="max-h-80 overflow-y-auto">
              {route.segments.map((segment: RouteSegment, index: number) => {
                // Calculate estimated time based on distance (assuming 1.4 m/s walking speed)
                const distanceInMeters =
                  typeof segment.distance === "number"
                    ? segment.distance
                    : segment.distance
                      ? parseFloat(
                          String(segment.distance).replace(/[^0-9.]/g, ""),
                        )
                      : 0;
                const timeInMinutes = Math.max(
                  1,
                  Math.round(distanceInMeters / 84),
                ); // 84 meters per minute

                return (
                  <div
                    key={index}
                    className="p-4 border-b border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      {/* Walking Icon */}
                      <div className="flex-shrink-0 pt-1">
                        <svg
                          className="w-6 h-6 text-gray-700"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M13.5 5.5c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zM9.8 8.9L7 23h2.1l1.8-8 2.1 2v6h2v-7.5l-2.1-2 .6-3C14.8 12 16.8 13 19 13v-2c-1.9 0-3.5-1-4.3-2.4l-1-1.6c-.4-.6-1-1-1.7-1-.3 0-.5.1-.8.1L6 8.3V13h2V9.6l1.8-.7" />
                        </svg>
                      </div>

                      {/* Route Info */}
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-900 leading-snug">
                          {segment.instruction}
                        </p>
                      </div>

                      {/* Time on Right */}
                      <div className="flex-shrink-0 text-right pt-1">
                        <p className="text-sm font-semibold text-gray-900">
                          {timeInMinutes} min
                        </p>
                        {distanceInMeters > 0 && (
                          <p className="text-xs text-gray-400 mt-1">
                            {distanceInMeters}m
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

      {/* Popular Places Section - Show when no route or always visible */}
      <div className="flex-1 overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 bg-white flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-800">
            Popular Places
          </h2>
          <button
            onClick={onBack}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ArrowLeft size={20} className="text-gray-600" />
          </button>
        </div>

        {/* Places List */}
        <div className="flex-1 overflow-y-auto">
          {/* Landmarks Section */}
          <div className="border-b border-gray-200">
            <button
              onClick={() => setLandmarksCollapsed(!landmarksCollapsed)}
              className="w-full flex items-center justify-between gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200 hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-center gap-2">
                <MapPin size={16} className="text-red-500" />
                <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                  Landmarks
                </h3>
                <span className="px-2 py-0.5 bg-gray-200 text-gray-700 rounded-full text-xs font-medium">
                  {landmarks.length}
                </span>
              </div>
              {landmarksCollapsed ? (
                <ChevronDown size={16} className="text-gray-600" />
              ) : (
                <ChevronUp size={16} className="text-gray-600" />
              )}
            </button>

            <AnimatePresence>
              {!landmarksCollapsed && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="p-2">
                    <div className="grid grid-cols-2 gap-2">
                      {landmarks.map((landmark, index) => {
                        const coordinateStatus = getCoordinateStatus(landmark);
                        return (
                          <motion.button
                            key={landmark.id}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: index * 0.02 }}
                            whileHover={{
                              scale: 1.05,
                              backgroundColor: "#f0f9ff",
                            }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => {
                              onSelectLandmark(landmark);
                              setDestinationSearchQuery(landmark.name);
                            }}
                            className="bg-white border border-gray-200 rounded-lg p-2 hover:border-blue-300 hover:shadow-md transition-all text-left relative"
                          >
                            <p className="text-xs font-semibold text-gray-900 line-clamp-2 leading-tight">
                              {landmark.name}
                            </p>
                            {/* Coordinate status indicator */}
                            <div className="absolute top-1 right-1">
                              <div
                                className={`w-2 h-2 rounded-full ${
                                  coordinateStatus.status === "accurate"
                                    ? "bg-green-500"
                                    : "bg-yellow-500"
                                }`}
                                title={coordinateStatus.message}
                              />
                            </div>
                          </motion.button>
                        );
                      })}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Departments Section */}
          <div>
            <button
              onClick={() => setDepartmentsCollapsed(!departmentsCollapsed)}
              className="w-full flex items-center justify-between gap-2 px-3 py-2 bg-gray-50 border-b border-gray-200 hover:bg-gray-100 transition-colors"
            >
              <div className="flex items-center gap-2">
                <Building size={16} className="text-blue-500" />
                <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                  Departments
                </h3>
                <span className="px-2 py-0.5 bg-gray-200 text-gray-700 rounded-full text-xs font-medium">
                  {departments.length}
                </span>
              </div>
              {departmentsCollapsed ? (
                <ChevronDown size={16} className="text-gray-600" />
              ) : (
                <ChevronUp size={16} className="text-gray-600" />
              )}
            </button>

            <AnimatePresence>
              {!departmentsCollapsed && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="p-2">
                    <div className="grid grid-cols-2 gap-2">
                      {departments.map((dept, index) => {
                        const coordinateStatus = getCoordinateStatus(dept);
                        return (
                          <motion.button
                            key={dept.id}
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: index * 0.02 }}
                            whileHover={{
                              scale: 1.05,
                              backgroundColor: "#f0f9ff",
                            }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => {
                              onSelectDepartment(dept);
                              setDestinationSearchQuery(dept.name);
                            }}
                            className="bg-white border border-gray-200 rounded-lg p-2 hover:border-blue-300 hover:shadow-md transition-all text-left relative"
                          >
                            <p className="text-xs font-semibold text-gray-900 line-clamp-2 leading-tight">
                              {dept.name}
                            </p>
                            {/* Coordinate status indicator */}
                            <div className="absolute top-1 right-1">
                              <div
                                className={`w-2 h-2 rounded-full ${
                                  coordinateStatus.status === "accurate"
                                    ? "bg-green-500"
                                    : "bg-yellow-500"
                                }`}
                                title={coordinateStatus.message}
                              />
                            </div>
                          </motion.button>
                        );
                      })}
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Navigation3DPanel;

